import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { posApi } from '../../api';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

/**
 * FR-POS-01.17 — Customer-facing display page.
 *
 * This page is designed to run on a secondary monitor facing the customer.
 * It polls the backend once per second and renders:
 *   - Idle: welcome message
 *   - Cart: live itemized list + running total
 *   - Thank-you: receipt number, change due, loyalty earned
 *
 * Accessible without auth intentionally so the physical screen can be on a
 * separate kiosk without a login.
 *
 * TODO: In a follow-up, we can switch to Server-Sent Events to avoid polling.
 * For now, 1 rps per terminal is negligible.
 */
export function CustomerDisplayPage() {
  const { tenantId, terminalCode } = useParams<{ tenantId: string; terminalCode: string }>();
  const [state, setState] = useState<any>(null);

  useEffect(() => {
    if (!tenantId || !terminalCode) return;
    let active = true;
    const tick = async () => {
      try {
        const s = await posApi.getCustomerDisplay(tenantId, terminalCode);
        if (active) setState(s);
      } catch {
        // Ignore transient errors — just keep last state
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => { active = false; clearInterval(id); };
  }, [tenantId, terminalCode]);

  if (!state) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
        <div className="text-2xl opacity-50">Connecting…</div>
      </div>
    );
  }

  if (state.mode === 'thank_you') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-600 to-emerald-800 text-white flex flex-col items-center justify-center p-8">
        <div className="text-8xl mb-6">✓</div>
        <div className="text-5xl font-bold mb-4">{state.message ?? 'Thank you!'}</div>
        {state.receiptNumber && (
          <div className="text-xl opacity-80 mb-2">Receipt {state.receiptNumber}</div>
        )}
        {typeof state.grandTotal === 'number' && (
          <div className="text-2xl mb-2">Total: ${state.grandTotal.toFixed(2)}</div>
        )}
        {typeof state.changeDue === 'number' && state.changeDue > 0 && (
          <div className="text-3xl font-semibold mt-4 bg-white bg-opacity-20 px-6 py-3 rounded-lg">
            Change: ${state.changeDue.toFixed(2)}
          </div>
        )}
        {state.loyaltyEarned ? (
          <div className="text-lg mt-6 opacity-90">
            ⭐ Earned {state.loyaltyEarned} loyalty point{state.loyaltyEarned === 1 ? '' : 's'}
          </div>
        ) : null}
      </div>
    );
  }

  if (state.mode === 'idle') {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-8">
        <div className="text-6xl font-bold mb-4">Welcome</div>
        <div className="text-2xl opacity-60">{state.message ?? 'Please wait for the cashier'}</div>
      </div>
    );
  }

  // mode === 'cart'
  return (
    <div className="min-h-screen bg-canvas-subtle flex flex-col">
      <div className="bg-slate-900 text-white p-4 flex justify-between items-center">
        <div className="text-2xl font-bold">Your Order</div>
        <div className="text-sm opacity-60">Terminal {terminalCode}</div>
      </div>
      <div className="flex-1 p-6 overflow-y-auto">
        {(state.lines ?? []).length === 0 ? (
          <div className="text-center text-slate-400 text-xl mt-16">Waiting for items…</div>
        ) : (
          <table className="w-full text-lg">
            <thead>
              <tr className="text-left border-b-2 border-slate-300">
                <th className="py-2">Item</th>
                <th className="text-right py-2">Qty</th>
                <th className="text-right py-2">Unit</th>
                <th className="text-right py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {state.lines.map((ln: any, i: number) => (
                <tr key={i} className="border-b border-slate-200">
                  <td className="py-2">
                    <div className="font-medium">{ln.name}</div>
                    <div className="text-xs text-slate-500">{ln.sku}</div>
                  </td>
                  <td className="text-right">{ln.quantity}</td>
                  <td className="text-right">{fmtCurrency(ln.unitPrice)}</td>
                  <td className="text-right font-semibold">{fmtCurrency(ln.lineTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="bg-white border-t border-slate-300 p-6 shadow-lg">
        {typeof state.subtotal === 'number' && (
          <div className="flex justify-between text-lg mb-1">
            <span className="text-slate-600">Subtotal</span>
            <span>{fmtCurrency(state.subtotal)}</span>
          </div>
        )}
        {typeof state.discount === 'number' && state.discount > 0 && (
          <div className="flex justify-between text-lg mb-1 text-emerald-700">
            <span>Discount</span>
            <span>-${state.discount.toFixed(2)}</span>
          </div>
        )}
        {typeof state.tax === 'number' && (
          <div className="flex justify-between text-lg mb-1">
            <span className="text-slate-600">Tax</span>
            <span>{fmtCurrency(state.tax)}</span>
          </div>
        )}
        {typeof state.grandTotal === 'number' && (
          <div className="flex justify-between text-3xl font-bold pt-2 border-t border-slate-300 mt-2">
            <span>Total</span>
            <span>{fmtCurrency(state.grandTotal)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
